import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Select,
  Row,
  Col,
  Statistic,
  Typography,
  Avatar,
  Badge,
  Tooltip,
  Drawer,
  Tabs,
  Descriptions,
  Spin,
} from "antd";
import {
  SearchOutlined,
  EditOutlined,
  UserOutlined,
  WalletOutlined,
  CarOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  ReloadOutlined,
  EyeOutlined,
  WarningOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  BankOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { getParkPassUsers, updateParkPassUser } from "../../api/parkpass/parkpassUsers";
import { getAdminWalletTransactions } from "../../api/parkpass/parkpassWalletTransactions";
import { getDisputes } from "../../api/parkpass/parkpassDisputes";
import { getParkingTickets } from "../../api/parkpass/parkpassParkingTickets";
import { getAdminCMIPayments } from "../../api/parkpass/parkpassCMIPayments";
import { getAdminFatouratiPayments } from "../../api/parkpass/parkpassFatouratiPayments";
import { getLogsByUser } from "../../api/parkpass/parkpassLogs";

const { Title, Text } = Typography;
const { Option } = Select;

export default function CitizenUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const workerExcelRef = useRef(null);

  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("profil");
  const [detailData, setDetailData] = useState({});
  const [detailPagination, setDetailPagination] = useState({
    transactions: { page: 1, total: 0 },
    disputes: { page: 1, total: 0 },
    tickets: { page: 1, total: 0 },
    cmi: { page: 1, total: 0 },
    fatourati: { page: 1, total: 0 },
    logs: { page: 1, total: 0 },
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url)
    );

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerExcelRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    let filtered = [...users];

    // Filter by search text
    if (searchText) {
      filtered = filtered.filter(
        (user) =>
          user.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.last_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.phone?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Filter by role
    if (selectedRole !== "all") {
      filtered = filtered.filter((user) => user.role_name === selectedRole);
    }

    setFilteredUsers(filtered);
  }, [searchText, selectedRole, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getParkPassUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      message.error("Erreur lors du chargement des utilisateurs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone,
      address: user.address,
    });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await updateParkPassUser(editingUser.user_id, values);
      message.success("Utilisateur mis à jour avec succès");

      // Refresh the list
      await fetchUsers();
      handleModalCancel();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error("Erreur lors de la mise à jour de l'utilisateur");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelClick = async () => {
    setIsExportingExcel(true);
    try {
      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Utilisateurs_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      // Prepare the data for export
      const formattedData = filteredUsers.map((user) => ({
        id: user.user_id || "-",
        nom: `${user.first_name || ""} ${user.last_name || ""}`.trim() || "-",
        email: user.email || "-",
        telephone: user.phone || "-",
        adresse: user.address || "-",
        role: user.role_name || "-",
        portefeuille: user.wallet?.balance?.toFixed(2) || "0.00",
        vehicules: user.vehicles?.length || 0,
      }));

      const data = [
        ["Utilisateurs Citizen Wallet", ``, ``, ``],
        [],
        [],
        "Rapport d'exportation",
        [],
        [
          [
            {
              text: "ID",
              type: "string",
              key: "id",
            },
            {
              text: "Nom",
              type: "string",
              key: "nom",
            },
            {
              text: "Email",
              type: "string",
              key: "email",
            },
            {
              text: "Téléphone",
              type: "string",
              key: "telephone",
            },
            {
              text: "Adresse",
              type: "string",
              key: "adresse",
            },
            {
              text: "Rôle",
              type: "string",
              key: "role",
            },
            {
              text: "Portefeuille (MAD)",
              type: "string",
              key: "portefeuille",
            },
            {
              text: "Nombre de Véhicules",
              type: "string",
              key: "vehicules",
            },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      // Post message to worker
      workerExcelRef.current.postMessage({ data, filename });
    } catch (error) {
      message.error("Erreur lors de la génération du fichier Excel");
      console.error(error);
    } finally {
      setIsExportingExcel(false);
    }
  };
  
  const handleViewDetail = async (user) => {
    setDetailUser(user);
    setActiveTabKey("profil");
    setDetailDrawerVisible(true);
    setDetailData({});
    setDetailLoading(true);
    const initPagination = {
      transactions: { page: 1, total: 0 },
      disputes: { page: 1, total: 0 },
      tickets: { page: 1, total: 0 },
      cmi: { page: 1, total: 0 },
      fatourati: { page: 1, total: 0 },
      logs: { page: 1, total: 0 },
    };
    setDetailPagination(initPagination);
    await fetchAllDetailTabs(user.user_id, initPagination);
    setDetailLoading(false);
  };

  const DETAIL_PAGE_SIZE = 10;

  const fetchAllDetailTabs = async (uid, pagination) => {
    const [txRes, disputeRes, ticketRes, cmiRes, fatRes, logsRes] = await Promise.allSettled([
      getAdminWalletTransactions({ userId: uid, page: pagination.transactions.page, pageSize: DETAIL_PAGE_SIZE }),
      getDisputes({ user_id: uid, page: pagination.disputes.page, page_size: DETAIL_PAGE_SIZE }),
      getParkingTickets({ userId: uid, page: pagination.tickets.page, pageSize: DETAIL_PAGE_SIZE }),
      getAdminCMIPayments({ userId: uid, page: pagination.cmi.page, pageSize: DETAIL_PAGE_SIZE }),
      getAdminFatouratiPayments({ userId: uid, page: pagination.fatourati.page, pageSize: DETAIL_PAGE_SIZE }),
      getLogsByUser(uid, { page: pagination.logs.page, pageSize: DETAIL_PAGE_SIZE }),
    ]);
    setDetailData({
      transactions: txRes.status === "fulfilled" ? (txRes.value?.items || []) : [],
      disputes: disputeRes.status === "fulfilled" ? (disputeRes.value?.data?.items || disputeRes.value?.data || []) : [],
      tickets: ticketRes.status === "fulfilled" ? (ticketRes.value?.items || []) : [],
      cmi: cmiRes.status === "fulfilled" ? (cmiRes.value?.items || []) : [],
      fatourati: fatRes.status === "fulfilled" ? (fatRes.value?.items || []) : [],
      logs: logsRes.status === "fulfilled" ? (logsRes.value?.rows || []) : [],
    });
    setDetailPagination({
      transactions: { page: pagination.transactions.page, total: txRes.status === "fulfilled" ? (txRes.value?.total || 0) : 0 },
      disputes: { page: pagination.disputes.page, total: disputeRes.status === "fulfilled" ? (disputeRes.value?.data?.total || 0) : 0 },
      tickets: { page: pagination.tickets.page, total: ticketRes.status === "fulfilled" ? (ticketRes.value?.total || 0) : 0 },
      cmi: { page: pagination.cmi.page, total: cmiRes.status === "fulfilled" ? (cmiRes.value?.total || 0) : 0 },
      fatourati: { page: pagination.fatourati.page, total: fatRes.status === "fulfilled" ? (fatRes.value?.total || 0) : 0 },
      logs: { page: pagination.logs.page, total: logsRes.status === "fulfilled" ? (logsRes.value?.totalRows || 0) : 0 },
    });
  };

  const fetchDetailTab = async (tabKey, page) => {
    if (!detailUser) return;
    const uid = detailUser.user_id;
    setTabLoading(true);
    try {
      let result;
      switch (tabKey) {
        case "transactions":
          result = await getAdminWalletTransactions({ userId: uid, page, pageSize: DETAIL_PAGE_SIZE });
          setDetailData(prev => ({ ...prev, transactions: result?.items || [] }));
          setDetailPagination(prev => ({ ...prev, transactions: { page, total: result?.total || 0 } }));
          break;
        case "disputes":
          result = await getDisputes({ user_id: uid, page, page_size: DETAIL_PAGE_SIZE });
          setDetailData(prev => ({ ...prev, disputes: result?.data?.items || result?.data || [] }));
          setDetailPagination(prev => ({ ...prev, disputes: { page, total: result?.data?.total || 0 } }));
          break;
        case "tickets":
          result = await getParkingTickets({ userId: uid, page, pageSize: DETAIL_PAGE_SIZE });
          setDetailData(prev => ({ ...prev, tickets: result?.items || [] }));
          setDetailPagination(prev => ({ ...prev, tickets: { page, total: result?.total || 0 } }));
          break;
        case "cmi":
          result = await getAdminCMIPayments({ userId: uid, page, pageSize: DETAIL_PAGE_SIZE });
          setDetailData(prev => ({ ...prev, cmi: result?.items || [] }));
          setDetailPagination(prev => ({ ...prev, cmi: { page, total: result?.total || 0 } }));
          break;
        case "fatourati":
          result = await getAdminFatouratiPayments({ userId: uid, page, pageSize: DETAIL_PAGE_SIZE });
          setDetailData(prev => ({ ...prev, fatourati: result?.items || [] }));
          setDetailPagination(prev => ({ ...prev, fatourati: { page, total: result?.total || 0 } }));
          break;
        case "logs":
          result = await getLogsByUser(uid, { page, pageSize: DETAIL_PAGE_SIZE });
          setDetailData(prev => ({ ...prev, logs: result?.rows || [] }));
          setDetailPagination(prev => ({ ...prev, logs: { page, total: result?.totalRows || 0 } }));
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setTabLoading(false);
    }
  };

  // Get unique roles for filter
  const roles = [...new Set(users.map((user) => user.role_name))];

  // Statistics
  const totalUsers = users.length;
  const totalBalance = users.reduce((sum, user) => sum + (user.wallet?.balance || 0), 0);
  const totalVehicles = users.reduce((sum, user) => sum + (user.vehicles?.length || 0), 0);

  const columns = [
    {
      title: "Utilisateur",
      key: "user",
      width: 280,
      fixed: "left",
      render: (_, record) => (
        <Space>
          <Avatar
            size={40}
            style={{ backgroundColor: "#722ed1" }}
            icon={<UserOutlined />}
          >
            {record.first_name?.[0]}{record.last_name?.[0]}
          </Avatar>
          <div>
            <Text strong>
              {record.first_name} {record.last_name}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              ID: {record.user_id}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      render: (email) => (
        <Space>
          <MailOutlined style={{ color: "#1890ff" }} />
          <Text copyable>{email}</Text>
        </Space>
      ),
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phone",
      width: 150,
      render: (phone) =>
        phone ? (
          <Space>
            <PhoneOutlined style={{ color: "#52c41a" }} />
            <Text>{phone}</Text>
          </Space>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Adresse",
      dataIndex: "address",
      key: "address",
      width: 200,
      ellipsis: true,
      render: (address) =>
        address ? (
          <Tooltip title={address}>
            <Space>
              <HomeOutlined style={{ color: "#faad14" }} />
              <Text ellipsis>{address}</Text>
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
    {
      title: "Rôle",
      dataIndex: "role_name",
      key: "role_name",
      width: 120,
      render: (role) => {
        const colors = {
          superadmin: "red",
          admin: "orange",
          user: "blue",
        };
        return (
          <Tag color={colors[role] || "default"}>
            {role?.toUpperCase() || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "Portefeuille",
      key: "wallet",
      width: 150,
      align: "right",
      render: (_, record) => (
        <Badge
          count={record.wallet?.balance || 0}
          showZero
          style={{
            backgroundColor: record.wallet?.balance > 0 ? "#52c41a" : "#d9d9d9",
          }}
        >
          <Space>
            <WalletOutlined style={{ fontSize: 18, color: "#722ed1" }} />
            <Text strong>{record.wallet?.balance?.toFixed(2) || "0.00"} MAD</Text>
          </Space>
        </Badge>
      ),
    },
    {
      title: "Véhicules",
      key: "vehicles",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Tooltip
          title={
            record.vehicles?.length > 0
              ? record.vehicles.map((v) => v.plate_number).join(", ")
              : "Aucun véhicule"
          }
        >
          <Badge count={record.vehicles?.length || 0} showZero>
            <CarOutlined style={{ fontSize: 18, color: "#1890ff" }} />
          </Badge>
        </Tooltip>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            size="small"
          >
            Détails
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Modifier
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Title level={2}>
          <UserOutlined /> Utilisateurs Citizen Wallet
        </Title>
        <Text type="secondary">
          Gérez les utilisateurs de la plateforme Citizen Wallet
        </Text>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Utilisateurs"
              value={totalUsers}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Solde Total"
              value={totalBalance.toFixed(2)}
              suffix="MAD"
              prefix={<WalletOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Véhicules"
              value={totalVehicles}
              prefix={<CarOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Input
              placeholder="Rechercher par nom, email ou téléphone..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              value={selectedRole}
              onChange={setSelectedRole}
              style={{ width: 150 }}
            >
              <Option value="all">Tous les rôles</Option>
              {roles.map((role) => (
                <Option key={role} value={role}>
                  {role?.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Space>
          <Space wrap>
            <Button
              icon={<FileTextOutlined />}
              onClick={handleExcelClick}
              disabled={isExportingExcel}
              className="hover:border-green-700 hover:text-green-500"
            >
              Exporter Excel
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
              loading={loading}
            >
              Actualiser
            </Button>
          </Space>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredUsers.sort((a, b) => b.user_id - a.user_id)}
          rowKey="user_id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            total: filteredUsers.length,
            defaultPageSize: 10,
            showTotal: (total) => `Total: ${total} utilisateurs`,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            <span>Modifier l'utilisateur</span>
          </Space>
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        width={600}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="first_name"
                label="Prénom"
                rules={[
                  { required: true, message: "Le prénom est requis" },
                  { min: 2, message: "Le prénom doit contenir au moins 2 caractères" },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Prénom" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="last_name"
                label="Nom"
                rules={[
                  { required: true, message: "Le nom est requis" },
                  { min: 2, message: "Le nom doit contenir au moins 2 caractères" },
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Nom" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "L'email est requis" },
              { type: "email", message: "Email invalide" },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Téléphone"
            rules={[
              { pattern: /^[0-9+\s()-]+$/, message: "Numéro de téléphone invalide" },
            ]}
          >
            <Input prefix={<PhoneOutlined />} placeholder="Téléphone" />
          </Form.Item>

          <Form.Item name="address" label="Adresse">
            <Input.TextArea
              prefix={<HomeOutlined />}
              placeholder="Adresse complète"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer
        title={
          detailUser && (
            <Space>
              <Avatar size={36} style={{ backgroundColor: "#722ed1" }} icon={<UserOutlined />} />
              <span>{detailUser.first_name} {detailUser.last_name}</span>
              <Tag color="purple">{detailUser.role_name?.toUpperCase()}</Tag>
            </Space>
          )
        }
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
        width={900}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", paddingTop: 80 }}><Spin size="large" /></div>
        ) : detailUser && (
          <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} size="small">

            {/* ── PROFIL ── */}
            <Tabs.TabPane tab={<span><UserOutlined /> Profil</span>} key="profil">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="ID">{detailUser.user_id}</Descriptions.Item>
                <Descriptions.Item label="Prénom">{detailUser.first_name}</Descriptions.Item>
                <Descriptions.Item label="Nom">{detailUser.last_name}</Descriptions.Item>
                <Descriptions.Item label="Email">{detailUser.email}</Descriptions.Item>
                <Descriptions.Item label="Téléphone">{detailUser.phone || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Adresse">{detailUser.address || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Rôle"><Tag color="purple">{detailUser.role_name}</Tag></Descriptions.Item>
                <Descriptions.Item label="Créé le">{detailUser.created_at ? new Date(detailUser.created_at).toLocaleString("fr-FR") : "N/A"}</Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>

            {/* ── PORTEFEUILLE ── */}
            <Tabs.TabPane tab={<span><WalletOutlined /> Portefeuille</span>} key="wallet">
              {detailUser.wallet ? (
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Wallet ID">{detailUser.wallet.wallet_id}</Descriptions.Item>
                  <Descriptions.Item label="Solde"><strong style={{ color: "#52c41a" }}>{parseFloat(detailUser.wallet.balance || 0).toFixed(2)} MAD</strong></Descriptions.Item>
                  <Descriptions.Item label="Solde Bloqué"><strong style={{ color: "#fa8c16" }}>{parseFloat(detailUser.wallet.locked_balance || 0).toFixed(2)} MAD</strong></Descriptions.Item>
                  <Descriptions.Item label="Solde Disponible"><strong style={{ color: "#1890ff" }}>{parseFloat(detailUser.wallet.available_balance || 0).toFixed(2)} MAD</strong></Descriptions.Item>
                  <Descriptions.Item label="Créé le">{detailUser.wallet.created_at ? new Date(detailUser.wallet.created_at).toLocaleString("fr-FR") : "N/A"}</Descriptions.Item>
                </Descriptions>
              ) : <p style={{ color: "#999" }}>Aucun portefeuille</p>}
            </Tabs.TabPane>

            {/* ── VÉHICULES ── */}
            <Tabs.TabPane tab={<span><CarOutlined /> Véhicules ({detailUser.vehicles?.length || 0})</span>} key="vehicles">
              <Table
                size="small"
                dataSource={detailUser.vehicles || []}
                rowKey="id"
                pagination={false}
                columns={[
                  { title: "Plaque", dataIndex: "plate_number", key: "plate_number", render: p => <Tag color="blue">{p}</Tag> },
                  { title: "Plaque Normalisée", dataIndex: "normalized_plate", key: "normalized_plate", render: v => v || "N/A" },
                  { title: "Country", dataIndex: "country", key: "country", render: v => v || "N/A" },
                  { title: "Caché", dataIndex: "is_hidden", key: "is_hidden", render: v => v ? <Tag color="red">Oui</Tag> : <Tag color="green">Non</Tag> },
                  { title: "Créé le", dataIndex: "created_at", key: "created_at", render: v => v ? new Date(v).toLocaleString("fr-FR") : "N/A" },
                ]}
              />
            </Tabs.TabPane>

            {/* ── TRANSACTIONS ── */}
            <Tabs.TabPane tab={<span><SwapOutlined /> Transactions ({detailPagination.transactions.total})</span>} key="transactions">
              <Table
                size="small"
                dataSource={detailData.transactions || []}
                rowKey="transaction_id"
                loading={tabLoading}
                pagination={{
                  current: detailPagination.transactions.page,
                  total: detailPagination.transactions.total,
                  pageSize: DETAIL_PAGE_SIZE,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                  onChange: (p) => fetchDetailTab("transactions", p),
                }}
                columns={[
                  { title: "ID", dataIndex: "transaction_id", key: "id", width: 60 },
                  { title: "Montant", dataIndex: "amount", key: "amount", render: a => <span style={{ color: a < 0 ? "#f5222d" : "#52c41a", fontWeight: 600 }}>{parseFloat(a).toFixed(2)} MAD</span> },
                  { title: "Type", dataIndex: "type", key: "type", render: t => <Tag>{t}</Tag> },
                  { title: "Statut", dataIndex: "status", key: "status", render: s => <Tag color={s === "completed" ? "green" : s === "pending" ? "orange" : "red"}>{s}</Tag> },
                  { title: "Date", dataIndex: "created_at", key: "created_at", render: d => d ? new Date(d).toLocaleString("fr-FR") : "N/A" },
                ]}
              />
            </Tabs.TabPane>

            {/* ── TICKETS PARKING ── */}
            <Tabs.TabPane tab={<span><FileTextOutlined /> Tickets ({detailPagination.tickets.total})</span>} key="tickets">
              <Table
                size="small"
                dataSource={detailData.tickets || []}
                rowKey="id"
                loading={tabLoading}
                pagination={{
                  current: detailPagination.tickets.page,
                  total: detailPagination.tickets.total,
                  pageSize: DETAIL_PAGE_SIZE,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                  onChange: (p) => fetchDetailTab("tickets", p),
                }}
                columns={[
                  { title: "ID", dataIndex: "id", key: "id", width: 60 },
                  { title: "Plaque", dataIndex: "vehicle_plate", key: "plate", render: p => <Tag color="blue">{p}</Tag> },
                  { title: "Parking", dataIndex: "parking_name", key: "parking" },
                  { title: "Montant", dataIndex: "amount", key: "amount", render: a => `${parseFloat(a || 0).toFixed(2)} MAD` },
                  { title: "Statut", dataIndex: "status", key: "status", render: s => <Tag color={s === "active" ? "green" : s === "expired" ? "red" : "orange"}>{s}</Tag> },
                  { title: "Début", dataIndex: "start_time", key: "start", render: d => d ? new Date(d).toLocaleString("fr-FR") : "N/A" },
                ]}
              />
            </Tabs.TabPane>

            {/* ── CONTESTATIONS ── */}
            <Tabs.TabPane tab={<span><WarningOutlined /> Contestations ({detailPagination.disputes.total})</span>} key="disputes">
              <Table
                size="small"
                dataSource={detailData.disputes || []}
                rowKey="id"
                loading={tabLoading}
                pagination={{
                  current: detailPagination.disputes.page,
                  total: detailPagination.disputes.total,
                  pageSize: DETAIL_PAGE_SIZE,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                  onChange: (p) => fetchDetailTab("disputes", p),
                }}
                columns={[
                  { title: "ID", dataIndex: "id", key: "id", width: 60 },
                  { title: "Réf Infraction", dataIndex: "reference", key: "ref" },
                  { title: "Plaque", dataIndex: "matricule", key: "mat" },
                  { title: "Montant", dataIndex: "amount", key: "amount", render: a => `${a} MAD` },
                  { title: "Statut", dataIndex: "status", key: "status", render: s => <Tag color={s === "approved" ? "green" : s === "rejected" ? "red" : "orange"}>{s}</Tag> },
                  { title: "Date", dataIndex: "created_at", key: "date", render: d => d ? new Date(d).toLocaleString("fr-FR") : "N/A" },
                ]}
              />
            </Tabs.TabPane>

            {/* ── PAIEMENTS CMI ── */}
            <Tabs.TabPane tab={<span><CreditCardOutlined /> CMI ({detailPagination.cmi.total})</span>} key="cmi">
              <Table
                size="small"
                dataSource={detailData.cmi || []}
                rowKey="payment_id"
                loading={tabLoading}
                pagination={{
                  current: detailPagination.cmi.page,
                  total: detailPagination.cmi.total,
                  pageSize: DETAIL_PAGE_SIZE,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                  onChange: (p) => fetchDetailTab("cmi", p),
                }}
                columns={[
                  { title: "ID", dataIndex: "payment_id", key: "id", width: 60 },
                  { title: "Montant", dataIndex: "amount", key: "amount", render: a => `${parseFloat(a || 0).toFixed(2)} MAD` },
                  { title: "Type", dataIndex: "product_type", key: "type", render: t => <Tag>{t}</Tag> },
                  { title: "Statut", dataIndex: "status", key: "status", render: s => <Tag color={s === "completed" ? "green" : s === "pending" ? "orange" : "red"}>{s}</Tag> },
                  { title: "Signalé", dataIndex: "flagged_for_review", key: "flagged", render: f => f ? <Tag color="red">Oui</Tag> : <Tag>Non</Tag> },
                  { title: "Date", dataIndex: "created_at", key: "date", render: d => d ? new Date(d).toLocaleString("fr-FR") : "N/A" },
                ]}
              />
            </Tabs.TabPane>

            {/* ── PAIEMENTS FATOURATI ── */}
            <Tabs.TabPane tab={<span><BankOutlined /> Fatourati ({detailPagination.fatourati.total})</span>} key="fatourati">
              <Table
                size="small"
                dataSource={detailData.fatourati || []}
                rowKey="payment_id"
                loading={tabLoading}
                pagination={{
                  current: detailPagination.fatourati.page,
                  total: detailPagination.fatourati.total,
                  pageSize: DETAIL_PAGE_SIZE,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                  onChange: (p) => fetchDetailTab("fatourati", p),
                }}
                columns={[
                  { title: "ID", dataIndex: "payment_id", key: "id", width: 60 },
                  { title: "Montant", dataIndex: "amount", key: "amount", render: a => `${parseFloat(a || 0).toFixed(2)} MAD` },
                  { title: "Type", dataIndex: "product_type", key: "type", render: t => <Tag>{t}</Tag> },
                  { title: "Statut", dataIndex: "status", key: "status", render: s => <Tag color={s === "paid" ? "green" : s === "pending" ? "orange" : "red"}>{s}</Tag> },
                  { title: "Réf", dataIndex: "ref_fatourati", key: "ref", render: r => <span style={{ fontFamily: "monospace", fontSize: 11 }}>{r || "N/A"}</span> },
                  { title: "Date", dataIndex: "created_at", key: "date", render: d => d ? new Date(d).toLocaleString("fr-FR") : "N/A" },
                ]}
              />
            </Tabs.TabPane>

            {/* ── LOGS ── */}
            <Tabs.TabPane tab={<span><FileTextOutlined /> Logs ({detailPagination.logs.total})</span>} key="logs">
              <Table
                size="small"
                dataSource={detailData.logs || []}
                rowKey={(r, i) => `${r.timestamp}-${i}`}
                loading={tabLoading}
                pagination={{
                  current: detailPagination.logs.page,
                  total: detailPagination.logs.total,
                  pageSize: DETAIL_PAGE_SIZE,
                  showSizeChanger: false,
                  showTotal: (total) => `Total: ${total}`,
                  onChange: (p) => fetchDetailTab("logs", p),
                }}
                columns={[
                  { title: "Date", dataIndex: "timestamp", key: "ts", width: 160, render: d => d ? new Date(d).toLocaleString("fr-FR") : "N/A" },
                  { title: "Niveau", dataIndex: "level", key: "level", width: 80, render: l => <Tag color={l === "ERROR" ? "red" : l === "WARNING" ? "orange" : "blue"}>{l}</Tag> },
                  { title: "IP", dataIndex: "ip", key: "ip", width: 120 },
                  { title: "Requête", dataIndex: "request", key: "req", ellipsis: true },
                  { title: "Status", dataIndex: "status_code", key: "status", width: 70, render: c => c ? <Tag color={c >= 400 ? "red" : "green"}>{c}</Tag> : "-" },
                ]}
              />
            </Tabs.TabPane>

          </Tabs>
        )}
      </Drawer>
    </div>
  );
}
